/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { connect } from 'react-redux';

import ChevronDown from '@gef-ui/material-icons/ChevronDown';
import ChevronUp from '@gef-ui/material-icons/ChevronUp';

import AvatarHead from 'components/atoms/CustomSVGIcon/AvatarHead';
import ToggleButton from 'components/atoms/GefMui/ToggleButton';
import { ComponentLoader } from 'components/index';
import fetch from 'features/common/fetch';
import campaignNominations from 'features/views/campaignNominations';
import { deleteNomination, fetchPPZ, updateNominations } from 'features/views/campaignSubsellerNomination/actions';
import { getClientName } from 'utils/client';

const Seller = (props) => {
	const { seller, campaignId, nominatedSellers, count, channelId, regionId, agencyId, manager } = props;
	const {
		toggleSellerNomination,
		deleteNomination,
		updateNominations,
		fetchPPZ,
		updateSellerCounts,
		fetchNominatedSellers,
		subsellerHasBeenNominatedForSeller,
	} = props;

	const [subsellers, setSubsellers] = useState([]);
	const [nominatedSubsellers, setNominatedSubsellers] = useState({});
	const isSellerNominated = nominatedSellers[seller.login];

	const loadNominatedSubsellers = () => {
		fetchNominatedSellers(campaignId, channelId, regionId, agencyId, manager.login, seller.login).then(
			({ body }) => {
				const update = {};
				body.forEach((nomination) => {
					if (nomination.ppz) {
						update[nomination.ppz.login] = true;
					}
				});
				setNominatedSubsellers(update);
			},
		);
	};

	useEffect(() => {
		if (props.isDetailVisible) {
			fetchPPZ(channelId, regionId, agencyId, manager.login, seller.login).then(({ body }) => {
				setSubsellers(body);
			});
			loadNominatedSubsellers();
		}
	}, [
		fetchNominatedSellers,
		fetchPPZ,
		campaignId,
		channelId,
		regionId,
		agencyId,
		manager.login,
		seller.login,
		props.isDetailVisible,
	]);

	const handleShowSubsellers = useCallback(
		(e) => {
			if (seller.hasSubordinates) {
				e.stopPropagation();
				props.onDetailVisibilityChange();
			}
		},
		[seller, props.onDetailVisibilityChange],
	);

	const handleToggleSellerNomination = useCallback(
		(seller) => () => {
			// do not allow to change sellers nomination, when some subseller is nominated
			if (count) {
				return;
			}
			toggleSellerNomination(seller).then(() => {
				loadNominatedSubsellers();
			});
		},
		[count, toggleSellerNomination],
	);

	const handleToggleSubsellerNomination = useCallback(
		(subseller) => () => {
			if (nominatedSubsellers[subseller.login]) {
				deleteNomination(campaignId, subseller.login).then(() => {
					setNominatedSubsellers({ ...nominatedSubsellers, [subseller.login]: false });
					updateSellerCounts();
				});
			} else {
				updateNominations(campaignId, subseller.login).then(() => {
					setNominatedSubsellers({ ...nominatedSubsellers, [subseller.login]: true });
					updateSellerCounts();
					if (!isSellerNominated) {
						subsellerHasBeenNominatedForSeller(seller);
					}
				});
			}
		},
		[deleteNomination, updateNominations, nominatedSubsellers, isSellerNominated],
	);

	return (
		<>
			<TableRow
				sx={{
					'& td': {
						borderBottom: 'unset',
					},
					backgroundColor: !!nominatedSellers[seller.login] ? 'primary.shades.8p' : 'default',
				}}
				onClick={handleToggleSellerNomination(seller)}
			>
				<TableCell colSpan={2}>
					<ComponentLoader
						fetchStorePath={`${fetch.constants.urls.campaignNominations}.update.${seller.login}`}
						ignoreErrors
					>
						<TableRow sx={{ display: 'flex', justifyContent: 'space-between' }}>
							<TableCell sx={{ pl: 7.5 }}>
								<Stack direction="row" spacing={4}>
									<IconButton size="small" sx={{ cursor: 'default' }}>
										<AvatarHead />
									</IconButton>
									<Stack
										sx={{
											justifyContent: 'center',
										}}
									>
										{getClientName(seller)}
									</Stack>
								</Stack>
							</TableCell>
							<TableCell align="right">
								{seller.hasSubordinates && (
									<Stack
										direction="row"
										spacing={10}
										sx={{
											alignItems: 'center',
										}}
									>
										{seller.hasSubordinates && ` ${count ? count : 0}`}
										{props.isDetailVisible ? (
											<ChevronUp
												sx={{ color: 'primary.main', cursor: 'pointer', ml: 1 }}
												id="toggleIcon"
												onClick={handleShowSubsellers}
											/>
										) : (
											<ChevronDown
												sx={{ color: 'primary.main', cursor: 'pointer', ml: 1 }}
												id="toggleIcon"
												onClick={handleShowSubsellers}
											/>
										)}
									</Stack>
								)}
							</TableCell>
						</TableRow>
					</ComponentLoader>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell sx={{ py: 0 }} colSpan={9}>
					<Collapse in={props.isDetailVisible} unmountOnExit>
						<Box sx={{ mt: 1, mb: 2 }}>
							<TableRow>
								<Stack
									direction="row"
									spacing={2}
									useFlexGap
									sx={{
										flexWrap: 'wrap',
									}}
								>
									{subsellers
										.filter((subseller) => subseller.login !== seller.login)
										.map((subseller) => (
											<ComponentLoader
												key={subseller.login}
												fetchStorePath={`${fetch.constants.urls.campaignNominations}.update.${subseller.login}`}
												ignoreErrors
											>
												<ToggleButton
													key={subseller.login}
													label={getClientName(subseller)}
													onClick={handleToggleSubsellerNomination(subseller)}
													selected={!!nominatedSubsellers[subseller.login]}
													clickableWhenSelected
												/>
											</ComponentLoader>
										))}
								</Stack>
							</TableRow>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

Seller.propTypes = {
	agencyId: PropTypes.number,
	campaignId: PropTypes.number,
	channelId: PropTypes.number,
	count: PropTypes.number,
	deleteNomination: PropTypes.func,
	fetchNominatedSellers: PropTypes.func,
	fetchPPZ: PropTypes.func,
	isDetailVisible: PropTypes.bool,
	manager: PropTypes.object,
	nominatedSellers: PropTypes.object,
	onDetailVisibilityChange: PropTypes.func,
	regionId: PropTypes.number,
	seller: PropTypes.object,
	subsellerHasBeenNominatedForSeller: PropTypes.func,
	toggleSellerNomination: PropTypes.func,
	updateNominations: PropTypes.func,
	updateSellerCounts: PropTypes.func,
};

export default connect(null, {
	fetchPPZ,
	fetchNominatedSellers: campaignNominations.actions.fetchNominatedSellers,
	updateNominations,
	deleteNomination,
})(Seller);
